import React, { useState } from "react";
import { SESSION_MINUTES, SESSION_PRICE } from "../../../data/mentorshipData";
import {
  dayParts,
  formatLongDate,
  formatSlotTime,
  formatSlotWhen,
} from "../mentorshipUtils";
import { CheckIcon } from "./MentorParts";

const TOPIC_MAX_LENGTH = 200;

// Day / time picker built from the mentor's real open slots, plus order
// summary, terms and the pay button. Used as its own screen on mobile and in
// the sticky side panel on desktop (`stickyFooter={false}` there since the
// panel itself is already sticky).
const BookingPanel = ({
  mentor,
  days,
  availabilityStatus, // "loading" | "ready" | "error"
  selectedDate,
  selectedSlot,
  nurseName,
  onNurseName,
  nurseEmail,
  onNurseEmail,
  nursePhone,
  onNursePhone,
  contactValid,
  topic,
  onTopic,
  agreed,
  paying,
  onDate,
  onSlot,
  onToggleAgree,
  onPay,
  stickyFooter = true,
}) => {
  const ready = Boolean(
    selectedSlot && contactValid && agreed && !paying && mentor.bookable,
  );

  // Red border only kicks in once the nurse has actually left the field, so an
  // empty required field doesn't look like an error before they've typed anything.
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const nameInvalid = nameTouched && !nurseName.trim();
  const emailInvalid = emailTouched && !nurseEmail.trim();
  const times = days.find((day) => day.date === selectedDate)?.slots || [];

  let picker;
  if (availabilityStatus === "loading") {
    picker = <p className="ms-small">Loading open times…</p>;
  } else if (availabilityStatus === "error") {
    picker = (
      <p className="ms-small">
        We couldn&apos;t load this mentor&apos;s times. Please try again in a
        moment.
      </p>
    );
  } else if (!mentor.bookable) {
    picker = (
      <div className="ms-notice">
        {mentor.name} hasn&apos;t finished payment setup yet, so calls can&apos;t
        be booked right now. Please check back soon.
      </div>
    );
  } else if (days.length === 0) {
    picker = (
      <p className="ms-small">
        No open times right now. Please check back soon.
      </p>
    );
  } else {
    picker = (
      <>
        <h3 className="ms-h3 ms-mt-20">Pick a day</h3>
        <div className="ms-slots">
          {days.map(({ date }) => {
            const { weekday, day } = dayParts(date);
            return (
              <button
                key={date}
                type="button"
                className={`ms-slot ${selectedDate === date ? "is-on" : ""}`}
                aria-pressed={selectedDate === date}
                aria-label={formatLongDate(date)}
                onClick={() => onDate(date)}
              >
                <span className="d">{weekday}</span>
                <span className="n">{day}</span>
              </button>
            );
          })}
        </div>

        <h3 className="ms-h3 ms-mt-20">Pick a time</h3>
        {selectedDate ? (
          <div className="ms-times">
            {times.map((slot) => (
              <button
                key={slot.id}
                type="button"
                className={`ms-time ${
                  selectedSlot?.id === slot.id ? "is-on" : ""
                }`}
                aria-pressed={selectedSlot?.id === slot.id}
                onClick={() => onSlot(slot)}
              >
                {formatSlotTime(slot.start_time, slot.start_utc)}
              </button>
            ))}
          </div>
        ) : (
          <p className="ms-small">Choose a day first.</p>
        )}
      </>
    );
  }

  return (
    <div className="ms-booking">
      <h3 className="ms-h3">Your info</h3>
      <div className="ms-contact-fields">
        <label className="ms-field">
          <span className="ms-field-label">
            Full name <span className="ms-required">*</span>
          </span>
          <input
            type="text"
            className={`ms-input ${nameInvalid ? "is-invalid" : ""}`}
            value={nurseName}
            onChange={(event) => onNurseName(event.target.value)}
            onBlur={() => setNameTouched(true)}
            placeholder="Your full name"
            autoComplete="name"
          />
          {nameInvalid && (
            <span className="ms-field-error">Name is required.</span>
          )}
        </label>
        <label className="ms-field">
          <span className="ms-field-label">
            Email <span className="ms-required">*</span>
          </span>
          <input
            type="email"
            className={`ms-input ${emailInvalid ? "is-invalid" : ""}`}
            value={nurseEmail}
            onChange={(event) => onNurseEmail(event.target.value)}
            onBlur={() => setEmailTouched(true)}
            placeholder="you@example.com"
            autoComplete="email"
          />
          {emailInvalid && (
            <span className="ms-field-error">Email is required.</span>
          )}
        </label>
        <label className="ms-field">
          <span className="ms-field-label">
            Mobile <span className="ms-optional">(optional)</span>
          </span>
          <input
            type="tel"
            className="ms-input"
            value={nursePhone}
            onChange={(event) => onNursePhone(event.target.value)}
            placeholder="Your phone number"
            autoComplete="tel"
          />
        </label>
      </div>

      {picker}

      {mentor.bookable && days.length > 0 && (
        <div className="ms-mt-20">
          <label className="ms-h3" htmlFor="ms-topic">
            What would you like to discuss?{" "}
            <span className="ms-optional">(optional)</span>
          </label>
          <textarea
            id="ms-topic"
            className="ms-textarea"
            rows={3}
            maxLength={TOPIC_MAX_LENGTH}
            placeholder="e.g. Moving from staff RN to travel nursing, or negotiating my first NP offer"
            value={topic}
            onChange={(event) => onTopic(event.target.value)}
          />
          <div className="ms-small ms-textarea-count">
            {topic.length}/{TOPIC_MAX_LENGTH}
          </div>
        </div>
      )}

      <div className="ms-card ms-summary ms-mt-20">
        <div className="ms-srow">
          <span className="k">Mentor</span>
          <span className="v">{mentor.name}</span>
        </div>
        <div className="ms-srow">
          <span className="k">When</span>
          <span className="v">
            {selectedSlot ? formatSlotWhen(selectedSlot) : "Choose above"}
          </span>
        </div>
        {topic.trim() && (
          <div className="ms-srow">
            <span className="k">Topic</span>
            <span className="v ms-topic-value">{topic.trim()}</span>
          </div>
        )}
        <div className="ms-srow">
          <span className="k">Length</span>
          <span className="v">{SESSION_MINUTES} minutes</span>
        </div>
        <div className="ms-srow">
          <span className="k">Total</span>
          <span className="v">${SESSION_PRICE}</span>
        </div>
      </div>

      <p className="ms-small ms-disclaimer">
        Mentors share personal experience and education only. This is not
        licensed financial, tax, legal, or job-placement advice. MapMyPay does
        not guarantee employment at any hospital or organization.
      </p>

      <button
        type="button"
        role="checkbox"
        aria-checked={agreed}
        className="ms-agree"
        onClick={onToggleAgree}
      >
        <span className={`ms-box ${agreed ? "is-on" : ""}`}>
          {agreed && <CheckIcon />}
        </span>
        <span>
          I have read and agree to the mentorship terms and booking policy.
        </span>
      </button>

      <div className={stickyFooter ? "ms-sticky" : "ms-footer"}>
        <button
          type="button"
          className="ms-btn ms-btn--primary ms-btn--lg ms-btn--block"
          disabled={!ready}
          onClick={onPay}
        >
          {paying ? "Redirecting to checkout…" : `Pay $${SESSION_PRICE} and confirm`}
        </button>
        <p className="ms-small ms-center ms-mt-8">
          One-time payment. Secure checkout powered by Stripe.
        </p>
      </div>
    </div>
  );
};

export default BookingPanel;
